import type { AlertSourceDetails } from '@kirin/api-client/contracts/alerts';

/**
 * The delimiter `formatToolLocation` (backend) inserts between an MCP tool's
 * `Tool: "<name>" (Server: <server>)` identity line and the head-truncated
 * description preview it appends.
 */
const TOOL_DESCRIPTION_MARKER = '\n\nTool description:';

/**
 * KIR-4175: for an MCP tool-description alert whose detection evidence is shown
 * elsewhere (the "More details" panel), drop the misleading head-truncated
 * description preview from the rendered `location`, leaving just the stable
 * `Tool: "<name>" (Server: <server>)` identity.
 *
 * Display-only: callers pass the raw `location` and render the result. The
 * stored/hashed `location` (and therefore the finding_key) is never touched.
 * Returns the input unchanged when there is no preview to trim.
 */
export function trimMcpToolLocationPreview(
  location: string | null | undefined
): string | null {
  if (location == null) return null;
  return location.split(TOOL_DESCRIPTION_MARKER)[0];
}

/**
 * Whether an alert/occurrence carries KIR-4175 MCP detection evidence — i.e. the
 * "More details" panel will render, so trimming the location preview is safe
 * (the full description is available there). False for non-MCP or non-regex
 * findings, and when the `alert_source_details` flag is off (no `sourceDetails`).
 */
export function hasMcpDetectionEvidence(
  sourceDetails: AlertSourceDetails | undefined
): boolean {
  return sourceDetails?.kind === 'mcp' && sourceDetails.matchedText != null;
}

/** The `Tool: "x" (Server: y)` identity plus the match window, for the Location block. */
export interface McpLocationEvidence {
  identity: string | null;
  before: string;
  match: string;
  after: string;
}

/**
 * KIR-4175: build the Location display for an MCP tool-description alert — the
 * stable tool/server identity plus the description **window around the match**
 * (rendered `before <mark>match</mark> after`) instead of the head-truncated
 * preview. Returns `null` when the finding has no match evidence, so the caller
 * falls back to the raw location. Display-only; never touches the stored value.
 */
export function getMcpLocationEvidence(
  sourceDetails: AlertSourceDetails | undefined,
  location: string | null | undefined
): McpLocationEvidence | null {
  if (sourceDetails?.kind !== 'mcp' || sourceDetails.matchedText == null) {
    return null;
  }
  return {
    identity: trimMcpToolLocationPreview(location),
    before: sourceDetails.matchedContextBefore ?? '',
    match: sourceDetails.matchedText,
    after: sourceDetails.matchedContextAfter ?? '',
  };
}
